import React from 'react';
import { cn } from '@ideoz/ui';
import type { DesignSystem } from './DesignSystemPanel';
import { borderRadiusToPx } from '../designSystemConstants';
import { loadGoogleFontPreview } from '../googleFonts';

export type DesignSystemLivePreviewProps = {
  designSystem: DesignSystem;
  logoUrl?: string | null;
  className?: string;
};

const SAMPLE_LIST_ITEMS = [
  { title: 'List item one', status: 'Active', tone: 'success' as const },
  { title: 'List item two', status: 'Draft', tone: 'muted' as const },
  { title: 'List item three', status: 'Pending', tone: 'warning' as const },
];

const PREVIEW_CARD_RADIUS = '12px';

function getDensityScale(density?: DesignSystem['density']) {
  switch (density) {
    case 'compact':
      return { rowPy: 6, rowPx: 12, sectionPy: 10, sectionPx: 12, fontSize: 11, gap: 6 };
    case 'spacious':
      return { rowPy: 14, rowPx: 16, sectionPy: 16, sectionPx: 16, fontSize: 13, gap: 12 };
    default:
      return { rowPy: 10, rowPx: 14, sectionPy: 12, sectionPx: 14, fontSize: 12, gap: 8 };
  }
}

function getCardShadow(designSystem: DesignSystem, isDark: boolean): string {
  const { elevationStyle, shadows } = designSystem;
  const shadowColor = isDark ? 'rgba(0, 0, 0, 0.35)' : 'rgba(15, 23, 42, 0.14)';
  const softColor = isDark ? 'rgba(0, 0, 0, 0.25)' : 'rgba(15, 23, 42, 0.08)';
  const smallColor = isDark ? 'rgba(0, 0, 0, 0.2)' : 'rgba(15, 23, 42, 0.06)';

  // Elevation style is the control surfaced on the design-system page, so it
  // drives the preview. Each style gets a distinct, visible treatment so the
  // choice is always reflected (glass = wide diffuse glow; neumorphism = dual
  // offset light/dark shadows).
  switch (elevationStyle) {
    case 'flat':
      return 'none';
    case 'material':
      return `0 10px 28px ${shadowColor}`;
    case 'soft-shadow':
      return `0 4px 14px ${softColor}`;
    case 'glass':
      return isDark ? '0 8px 32px rgba(0, 0, 0, 0.45)' : '0 8px 32px rgba(15, 23, 42, 0.12)';
    case 'neumorphism':
      return isDark
        ? '6px 6px 14px rgba(0, 0, 0, 0.55), -6px -6px 14px rgba(255, 255, 255, 0.04)'
        : '6px 6px 14px rgba(15, 23, 42, 0.12), -6px -6px 14px rgba(255, 255, 255, 0.9)';
  }

  // No elevation style set — fall back to the legacy `shadows` scale.
  if (shadows === 'none') return 'none';
  if (shadows === 'large') return `0 10px 28px ${shadowColor}`;
  if (shadows === 'medium') return `0 4px 14px ${softColor}`;
  if (shadows === 'small') return `0 2px 8px ${smallColor}`;
  return `0 1px 3px ${isDark ? 'rgba(0, 0, 0, 0.15)' : 'rgba(15, 23, 42, 0.05)'}`;
}

function getInputStyles(
  inputStyle: DesignSystem['inputStyle'],
  border: string,
  surface: string,
  primary: string,
  radius: string,
  isDark: boolean
): React.CSSProperties {
  const textColor = isDark ? '#F3F4F6' : '#111827';
  const base: React.CSSProperties = {
    width: '100%',
    fontSize: 11,
    padding: '6px 10px',
    borderRadius: radius,
    outline: 'none',
    color: textColor,
  };

  switch (inputStyle) {
    case 'filled':
      return { ...base, backgroundColor: `${primary}10`, border: `1px solid transparent` };
    case 'underlined':
      return {
        ...base,
        backgroundColor: 'transparent',
        border: 'none',
        borderBottom: `1px solid ${border}`,
        borderRadius: 0,
        paddingLeft: 0,
      };
    case 'floating-label':
      return { ...base, backgroundColor: surface, border: `1px solid ${primary}55` };
    default:
      return { ...base, backgroundColor: surface, border: `1px solid ${border}` };
  }
}

export function DesignSystemLivePreview({
  designSystem,
  logoUrl,
  className,
}: DesignSystemLivePreviewProps): JSX.Element {
  const isDark = designSystem.theme === 'dark';
  const primary = designSystem.primaryColor ?? '#334155';
  const accent = designSystem.secondaryColor ?? '#64748B';
  const appBg = designSystem.appBg ?? (isDark ? '#111827' : '#F9FAFB');
  const surface = designSystem.surfaceColor ?? (isDark ? '#1F2937' : '#FFFFFF');
  const border = designSystem.borderColor ?? (isDark ? '#374151' : '#E5E7EB');
  const muted = designSystem.mutedTextColor ?? (isDark ? '#9CA3AF' : '#6B7280');
  const success = designSystem.successColor ?? '#22C55E';
  const warning = designSystem.warningColor ?? '#EAB308';
  const error = designSystem.errorColor ?? '#EF4444';
  const info = designSystem.infoColor ?? '#3B82F6';
  const bodyFont = designSystem.fontFamily ?? 'Exo 2';
  const headingFont = designSystem.headingFontFamily ?? bodyFont;
  const listPattern = designSystem.uxPatterns?.listPattern ?? 'table';
  const showSearch = designSystem.uxPatterns?.searchPattern === 'top-bar-inline';

  const density = getDensityScale(designSystem.density);
  const componentRadius = borderRadiusToPx(designSystem.borderRadius);
  const buttonRadius =
    designSystem.borderRadius === 'full' || designSystem.buttonStyle === 'pill'
      ? '9999px'
      : componentRadius;
  const cardShadow = getCardShadow(designSystem, isDark);
  const textPrimary = isDark ? '#F3F4F6' : '#111827';
  const textHeading = isDark ? '#F9FAFB' : '#111827';
  const raisedSurface = isDark ? '#374151' : '#FFFFFF';

  React.useEffect(() => {
    loadGoogleFontPreview(bodyFont);
    if (headingFont !== bodyFont) loadGoogleFontPreview(headingFont);
  }, [bodyFont, headingFont]);

  const renderListContent = () => {
    if (listPattern === 'cards') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: density.gap }}>
          {SAMPLE_LIST_ITEMS.map((item) => (
            <div
              key={item.title}
              style={{
                padding: `${density.rowPy}px ${density.rowPx}px`,
                borderRadius: componentRadius,
                border: `1px solid ${border}`,
                backgroundColor: raisedSurface,
              }}
            >
              <div className="flex items-center justify-between">
                <span style={{ fontSize: density.fontSize, fontWeight: 500, color: textPrimary }}>
                  {item.title}
                </span>
                <StatusBadge
                  item={item}
                  primary={primary}
                  muted={muted}
                  success={success}
                  warning={warning}
                  radius={buttonRadius}
                />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (listPattern === 'table') {
      return (
        <div
          style={{
            border: `1px solid ${border}`,
            borderRadius: componentRadius,
            overflow: 'hidden',
          }}
        >
          <div
            className="grid grid-cols-[1fr_auto]"
            style={{
              padding: `${density.rowPy - 2}px ${density.rowPx}px`,
              backgroundColor: `${muted}12`,
              borderBottom: `1px solid ${border}`,
              fontSize: 10,
              fontWeight: 600,
              color: muted,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            <span>Name</span>
            <span>Status</span>
          </div>
          {SAMPLE_LIST_ITEMS.map((item, idx) => (
            <div
              key={item.title}
              className="grid grid-cols-[1fr_auto] items-center"
              style={{
                padding: `${density.rowPy}px ${density.rowPx}px`,
                borderBottom:
                  idx < SAMPLE_LIST_ITEMS.length - 1 ? `1px solid ${border}` : undefined,
                fontSize: density.fontSize,
              }}
            >
              <span style={{ fontWeight: 500, color: textPrimary }}>{item.title}</span>
              <StatusBadge
                item={item}
                primary={primary}
                muted={muted}
                success={success}
                warning={warning}
                radius={buttonRadius}
              />
            </div>
          ))}
        </div>
      );
    }

    return (
      <div>
        {SAMPLE_LIST_ITEMS.map((item, idx) => (
          <div
            key={item.title}
            className="flex items-center justify-between"
            style={{
              padding: `${density.rowPy}px 0`,
              borderBottom: idx < SAMPLE_LIST_ITEMS.length - 1 ? `1px solid ${border}` : undefined,
            }}
          >
            <div>
              <p style={{ fontSize: density.fontSize, fontWeight: 500, color: textPrimary }}>
                {item.title}
              </p>
              <p style={{ fontSize: density.fontSize - 1, color: muted }}>Updated 2h ago</p>
            </div>
            <StatusBadge
              item={item}
              primary={primary}
              muted={muted}
              success={success}
              warning={warning}
              radius={buttonRadius}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <aside className={cn('flex h-full flex-col bg-white dark:bg-gray-900', className)}>
      <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">Live preview</span>
      </div>

      <div className="flex-1 overflow-auto bg-gray-50 p-4 dark:bg-gray-950/50">
        <p className="mb-3 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
          Sample UI reflecting colors, typography, radius, density, elevation, inputs, and semantic
          colors.
        </p>

        <div className="rounded-xl p-4" style={{ backgroundColor: appBg, fontFamily: bodyFont }}>
          <div
            className="overflow-hidden border"
            style={{
              borderRadius: PREVIEW_CARD_RADIUS,
              borderColor: border,
              backgroundColor: surface,
              boxShadow: cardShadow,
            }}
          >
            {/* App bar */}
            <div
              className="border-b"
              style={{
                borderColor: border,
                padding: `${density.sectionPy}px ${density.sectionPx}px`,
              }}
            >
              <div className="flex items-center justify-between gap-2">
                {logoUrl ? (
                  <img src={logoUrl} alt="Brand logo" className="h-6 max-w-[80px] object-contain" />
                ) : (
                  <span
                    className="text-sm font-semibold italic"
                    style={{ color: primary, fontFamily: headingFont }}
                  >
                    brand.
                  </span>
                )}
                <button
                  type="button"
                  className="shrink-0 px-2.5 py-1 text-[11px] font-medium text-white"
                  style={{ backgroundColor: primary, borderRadius: buttonRadius }}
                >
                  New item
                </button>
              </div>
              {showSearch && (
                <input
                  readOnly
                  placeholder="Search…"
                  className={cn(
                    'mt-2',
                    isDark ? 'placeholder:text-gray-500' : 'placeholder:text-gray-400'
                  )}
                  style={getInputStyles(
                    designSystem.inputStyle,
                    border,
                    surface,
                    primary,
                    componentRadius,
                    isDark
                  )}
                />
              )}
            </div>

            {/* List section */}
            <div style={{ padding: `${density.sectionPy}px ${density.sectionPx}px` }}>
              <p
                className="mb-0.5 font-semibold"
                style={{
                  fontSize: density.fontSize + 1,
                  color: textHeading,
                  fontFamily: headingFont,
                }}
              >
                Sample list view
              </p>
              <p className="mb-2 text-[10px]" style={{ color: muted }}>
                {listPattern === 'table'
                  ? 'Table layout'
                  : listPattern === 'cards'
                    ? 'Card grid'
                    : 'List rows'}{' '}
                · {designSystem.density ?? 'comfortable'} density
              </p>
              {renderListContent()}
            </div>

            {/* Input sample */}
            {!showSearch && (
              <div
                className="border-t"
                style={{
                  borderColor: border,
                  padding: `${density.sectionPy}px ${density.sectionPx}px`,
                }}
              >
                <p className="mb-1.5 text-[10px] font-medium" style={{ color: muted }}>
                  Input · {designSystem.inputStyle ?? 'outlined'}
                </p>
                <input
                  readOnly
                  placeholder="Sample field"
                  className={isDark ? 'placeholder:text-gray-500' : 'placeholder:text-gray-400'}
                  style={getInputStyles(
                    designSystem.inputStyle,
                    border,
                    surface,
                    primary,
                    componentRadius,
                    isDark
                  )}
                />
              </div>
            )}

            {/* Buttons */}
            <div
              className="flex flex-wrap items-center gap-2 border-t"
              style={{
                borderColor: border,
                padding: `${density.sectionPy}px ${density.sectionPx}px`,
              }}
            >
              <button
                type="button"
                className="px-2.5 py-1 text-[11px] font-medium text-white"
                style={{ backgroundColor: primary, borderRadius: buttonRadius }}
              >
                Primary
              </button>
              <button
                type="button"
                className="border px-2.5 py-1 text-[11px] font-medium"
                style={{
                  borderColor: accent,
                  color: primary,
                  backgroundColor: 'transparent',
                  borderRadius: buttonRadius,
                }}
              >
                Secondary
              </button>
              <button
                type="button"
                className="px-1 py-1 text-[11px] font-medium underline-offset-2"
                style={{ color: primary }}
              >
                Link
              </button>
            </div>

            {/* Semantic colors */}
            <div
              className="flex flex-wrap gap-1.5 border-t"
              style={{
                borderColor: border,
                padding: `${density.sectionPy}px ${density.sectionPx}px`,
              }}
            >
              {(
                [
                  { label: 'Success', color: success },
                  { label: 'Warning', color: warning },
                  { label: 'Error', color: error },
                  { label: 'Info', color: info },
                ] as const
              ).map(({ label, color }) => (
                <span
                  key={label}
                  className="px-2 py-0.5 text-[10px] font-medium"
                  style={{
                    borderRadius: buttonRadius,
                    backgroundColor: `${color}18`,
                    color,
                  }}
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="shrink-0 border-t border-gray-100 px-4 py-3 dark:border-gray-800 dark:bg-gray-900">
        <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">Applies to</p>
        <p className="mt-0.5 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
          Every prototype, artifact, and screen under this startup — including modals and CTA
          treatments.
        </p>
      </div>
    </aside>
  );
}

function StatusBadge({
  item,
  primary,
  muted,
  success,
  warning,
  radius,
}: {
  item: (typeof SAMPLE_LIST_ITEMS)[number];
  primary: string;
  muted: string;
  success: string;
  warning: string;
  radius: string;
}): JSX.Element {
  const color =
    item.tone === 'success'
      ? success
      : item.tone === 'warning'
        ? warning
        : item.tone === 'muted'
          ? muted
          : primary;

  return (
    <span
      className="px-2 py-0.5 text-[10px] font-medium"
      style={{
        borderRadius: radius,
        backgroundColor: `${color}18`,
        color,
      }}
    >
      {item.status}
    </span>
  );
}

export default DesignSystemLivePreview;
